import { Controller } from "@hotwired/stimulus"
import { serialise } from "../modules/salary_lab/cv_form_serialiser"

export default class extends Controller {
  static values = {
    url: String,
    previewPdfUrl: String,
    debounce: { type: Number, default: 1000 },
  }

  static targets = [
    "templatePicker",
    "editTab", "previewTab", "formPanel", "previewPanel",
    "pdfPreview", "pdfPreviewPanel",
  ]

  connect() {
    this._timer = null
    this._saving = false
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
  }

  onChange() {
    this._setStatus("Unsaved")
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => this._save(), this.debounceValue)
  }

  onAccentChange() {
    this.onChange()
  }

  toggleTemplatePicker() {
    if (!this.hasTemplatePickerTarget) return
    this.templatePickerTarget.classList.toggle("hidden")
  }

  showEditTab() {
    this._setActiveTab("edit")
  }

  showPreviewTab() {
    this._loadPreviewPanel()
    this._setActiveTab("preview")
  }

  async _save() {
    if (this._saving) return
    this._saving = true
    this._setStatus("Saving...")

    try {
      const content = serialise(this.element)
      const settings = this._readSettings()
      const token = document.querySelector('meta[name="csrf-token"]')?.content

      const body = { cv_document: { content } }
      if (Object.keys(settings).length > 0) body.cv_document.settings = settings

      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify(body),
      })

      if (response.ok) {
        this._setStatus("Saved")
        this._refreshPreview()
      } else {
        this._setStatus("Save failed")
      }
    } catch {
      this._setStatus("Save failed")
    } finally {
      this._saving = false
    }
  }

  _readSettings() {
    const settings = {}
    const accentInput = document.getElementById("accent_colour")
    if (accentInput) settings.accent_colour = accentInput.value
    return settings
  }

  _refreshPreview() {
    const url = this._cacheBustedPreviewUrl()
    if (this.hasPdfPreviewTarget) {
      this.pdfPreviewTarget.src = url
    }
    if (this.hasPdfPreviewPanelTarget && !this.previewPanelTarget.classList.contains("hidden")) {
      this.pdfPreviewPanelTarget.src = url
    }
  }

  _loadPreviewPanel() {
    if (!this.hasPdfPreviewPanelTarget) return
    this.pdfPreviewPanelTarget.src = this._cacheBustedPreviewUrl()
  }

  _cacheBustedPreviewUrl() {
    const url = new URL(this.previewPdfUrlValue, window.location.origin)
    url.searchParams.set("t", Date.now())
    return url.toString()
  }

  _setStatus(text) {
    const el = document.getElementById("save_status")
    if (!el) return

    el.textContent = text

    el.classList.remove("cv-status-saved", "cv-status-saving", "cv-status-error")
    if (text === "Saved") {
      el.classList.add("cv-status-saved")
      el.dataset.status = "saved"
    } else if (text === "Saving...") {
      el.classList.add("cv-status-saving")
      el.dataset.status = "saving"
    } else if (text === "Unsaved") {
      el.dataset.status = "unsaved"
    } else {
      el.classList.add("cv-status-error")
      el.dataset.status = "error"
    }
  }

  _setActiveTab(tab) {
    const activeClasses = "bg-white text-gray-800 shadow-sm"
    const inactiveClasses = "text-gray-500 hover:text-gray-700"

    if (this.hasEditTabTarget) {
      this.editTabTarget.className = this.editTabTarget.className
        .replace(activeClasses, "").replace(inactiveClasses, "").trim()
        + " " + (tab === "edit" ? activeClasses : inactiveClasses)
    }

    if (this.hasPreviewTabTarget) {
      this.previewTabTarget.className = this.previewTabTarget.className
        .replace(activeClasses, "").replace(inactiveClasses, "").trim()
        + " " + (tab === "preview" ? activeClasses : inactiveClasses)
    }

    if (this.hasFormPanelTarget) {
      this.formPanelTarget.classList.toggle("hidden", tab === "preview")
    }

    if (this.hasPreviewPanelTarget) {
      this.previewPanelTarget.classList.toggle("hidden", tab === "edit")
    }
  }
}
